/**
 * DbServerFormModal — panel ITE „Serwery baz" (dodawanie / edycja serwera PostgreSQL klienta).
 *
 * Pola: nazwa, adres, port, szyfrowanie, certyfikat CA (opcjonalny), konto aplikacji
 * (edytowalne tylko przy dodawaniu albo dla serwera nieprzygotowanego), notatka.
 * Błędy serwera mapowane na czytelne komunikaty (patrz docs/ADR/0113b §6).
 */

import { type FormEvent, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { Textarea } from '@/components/Textarea';
import { useToast } from '@/contexts/ToastContext';
import {
  type DbServerDto,
  dbServerErrorInfo,
  type DbServerSslMode,
  type DbServerUpdateInput,
  useCreateDbServer,
  useUpdateDbServer,
} from '@/lib/db-servers/api';

export interface DbServerFormModalProps {
  open: boolean;
  /** null = dodawanie nowego serwera. */
  server: DbServerDto | null;
  onClose: () => void;
  /** Dodawanie: nazwa podpowiedziana w polu (np. nazwa tenanta w jego karcie). */
  defaultName?: string;
  /** Dodawanie: wołane z nowym serwerem przed zamknięciem okna. */
  onCreated?: (server: DbServerDto) => void;
}

interface FormState {
  name: string;
  host: string;
  port: string;
  sslMode: DbServerSslMode;
  caCert: string;
  removeCaCert: boolean;
  appUser: string;
  notes: string;
}

function initialState(server: DbServerDto | null, defaultName?: string): FormState {
  return {
    name: server?.name ?? defaultName ?? '',
    host: server?.host ?? '',
    port: String(server?.port ?? 5432),
    sslMode: server?.sslMode ?? 'verify-full',
    caCert: '',
    removeCaCert: false,
    appUser: server?.appUser ?? 'saas',
    notes: server?.notes ?? '',
  };
}

/**
 * Wyciąga NAZWY pól z `details` odpowiedzi VALIDATION_FAILED (różne kształty). Surowych
 * komunikatów serwera (zod, po angielsku) nie pokazujemy — dla każdego pola dajemy komunikat z i18n.
 */
function invalidFieldsFrom(data: Record<string, unknown> | null): string[] {
  const details = data?.details as Record<string, unknown> | undefined;
  const raw = (details?.fieldErrors ?? details) as Record<string, unknown> | undefined;
  if (!raw || typeof raw !== 'object') return [];
  return Object.entries(raw)
    .filter(([, val]) => (Array.isArray(val) ? val.length > 0 : typeof val === 'string'))
    .map(([key]) => key);
}

export function DbServerFormModal({
  open,
  server,
  onClose,
  defaultName,
  onCreated,
}: DbServerFormModalProps) {
  if (!open) return null;
  return (
    <DbServerFormModalBody
      server={server}
      onClose={onClose}
      defaultName={defaultName}
      onCreated={onCreated}
    />
  );
}

function DbServerFormModalBody({
  server,
  onClose,
  defaultName,
  onCreated,
}: Omit<DbServerFormModalProps, 'open'>) {
  const { t } = useTranslation();
  const toast = useToast();
  const createMutation = useCreateDbServer();
  const updateMutation = useUpdateDbServer();
  const formId = useId();

  const [values, setValues] = useState<FormState>(() => initialState(server, defaultName));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const isEdit = server !== null;
  const appUserLocked = isEdit && server.status !== 'new';
  // Serwer z firmami: adres, port i szyfrowanie są zablokowane (serwer też tego pilnuje).
  const addressLocked = isEdit && server.tenantCount > 0;
  const saving = createMutation.isPending || updateMutation.isPending;

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setValues((prev) => ({ ...prev, [key]: value }));
  }

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!values.name.trim()) next.name = t('admin.dbServers.form.errors.nameRequired');
    if (!values.host.trim()) next.host = t('admin.dbServers.form.errors.hostRequired');
    const port = Number(values.port);
    if (!Number.isInteger(port) || port < 1 || port > 65535) {
      next.port = t('admin.dbServers.form.errors.portInvalid');
    }
    if (!appUserLocked && !values.appUser.trim()) {
      next.appUser = t('admin.dbServers.form.errors.appUserRequired');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleError(err: unknown) {
    const { code, data } = dbServerErrorInfo(err);
    switch (code) {
      case 'NAME_TAKEN':
        setErrors({ name: t('admin.dbServers.form.errors.nameTaken') });
        return;
      case 'VERIFY_REQUIRED_FOR_PUBLIC_HOST':
        setErrors({ sslMode: t('admin.dbServers.form.errors.verifyRequiredForPublicHost') });
        return;
      case 'ADDRESS_LOCKED':
        setFormError(t('admin.dbServers.form.errors.addressLocked'));
        return;
      case 'BUSY':
        setFormError(t('admin.dbServers.form.errors.busy'));
        return;
      case 'APP_USER_LOCKED':
        setErrors({ appUser: t('admin.dbServers.form.errors.appUserLocked') });
        return;
      case 'VALIDATION_FAILED': {
        const invalid = invalidFieldsFrom(data);
        setErrors(
          Object.fromEntries(
            invalid.map((k) => [k, t('admin.dbServers.form.errors.fieldInvalid')]),
          ),
        );
        if (invalid.length === 0) {
          setFormError(t('admin.dbServers.form.errors.validationFailed'));
        }
        return;
      }
      default:
        setFormError(t('admin.dbServers.form.errors.generic'));
    }
  }

  function handleSubmit(e?: FormEvent) {
    e?.preventDefault();
    setFormError(null);
    setErrors({});
    if (!validate()) return;

    const name = values.name.trim();
    const host = values.host.trim();
    const port = Number(values.port);
    const notes = values.notes.trim() || null;
    const caCert = values.caCert.trim();

    if (!isEdit) {
      createMutation.mutate(
        {
          name,
          host,
          port,
          sslMode: values.sslMode,
          caCert: caCert || null,
          appUser: values.appUser.trim(),
          notes,
        },
        {
          onSuccess: (created) => {
            toast.show({ variant: 'success', title: t('admin.dbServers.toast.created') });
            onCreated?.(created);
            onClose();
          },
          onError: handleError,
        },
      );
      return;
    }

    const input: DbServerUpdateInput = { name, notes };
    let connectionChanged = false;
    if (!addressLocked) {
      input.host = host;
      input.port = port;
      input.sslMode = values.sslMode;
      if (caCert) input.caCert = caCert;
      else if (values.removeCaCert) input.caCert = null;
      connectionChanged =
        host !== server.host ||
        port !== server.port ||
        values.sslMode !== server.sslMode ||
        !!caCert ||
        (values.removeCaCert && server.hasCaCert);
    }
    if (!appUserLocked && values.appUser.trim() !== server.appUser) {
      input.appUser = values.appUser.trim();
    }
    updateMutation.mutate(
      { id: server.id, input },
      {
        onSuccess: () => {
          toast.show({
            variant: 'success',
            title: t(
              connectionChanged
                ? 'admin.dbServers.toast.updatedRecheck'
                : 'admin.dbServers.toast.updated',
            ),
          });
          onClose();
        },
        onError: handleError,
      },
    );
  }

  return (
    <Modal
      open
      onClose={() => (saving ? undefined : onClose())}
      closeOnBackdrop={!saving}
      closeOnEsc={!saving}
      title={t(isEdit ? 'admin.dbServers.form.titleEdit' : 'admin.dbServers.form.titleCreate')}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            type="submit"
            form={formId}
            loading={saving}
            data-testid="db-server-form-submit"
          >
            {isEdit ? t('common.save') : t('common.create')}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        onSubmit={handleSubmit}
        noValidate
        className="flex flex-col gap-4"
        data-testid="db-server-form"
      >
        {addressLocked && (
          <p className="text-xs text-text-tertiary" data-testid="db-server-form-address-locked">
            {t('admin.dbServers.form.addressLockedHint')}
          </p>
        )}

        <FormField label={t('admin.dbServers.form.name')} required error={errors.name}>
          <Input
            value={values.name}
            onChange={(e) => set('name', e.target.value)}
            maxLength={200}
            data-testid="db-server-form-name"
          />
        </FormField>

        <div className="grid grid-cols-[minmax(0,1fr)_7rem] gap-3">
          <FormField label={t('admin.dbServers.form.host')} required error={errors.host}>
            <Input
              value={values.host}
              onChange={(e) => set('host', e.target.value)}
              placeholder="db.klient.pl"
              disabled={addressLocked}
              data-testid="db-server-form-host"
            />
          </FormField>
          <FormField label={t('admin.dbServers.form.port')} required error={errors.port}>
            <Input
              type="number"
              value={values.port}
              onChange={(e) => set('port', e.target.value)}
              min={1}
              max={65535}
              disabled={addressLocked}
              data-testid="db-server-form-port"
            />
          </FormField>
        </div>

        <FormField
          label={t('admin.dbServers.form.sslMode')}
          error={errors.sslMode}
          hint={t('admin.dbServers.form.sslModeHint')}
        >
          <Dropdown
            value={values.sslMode}
            onChange={(v) => set('sslMode', v as DbServerSslMode)}
            disabled={addressLocked}
            data-testid="db-server-form-ssl-mode"
            options={[
              { value: 'verify-full', label: t('admin.dbServers.sslMode.verify-full') },
              { value: 'require', label: t('admin.dbServers.sslMode.require') },
              { value: 'disable', label: t('admin.dbServers.sslMode.disable') },
            ]}
          />
        </FormField>

        <FormField
          label={t('admin.dbServers.form.caCert')}
          error={errors.caCert}
          hint={
            isEdit && server.hasCaCert
              ? t('admin.dbServers.form.caCertHintStored')
              : t('admin.dbServers.form.caCertHint')
          }
        >
          <Textarea
            value={values.caCert}
            onChange={(e) => set('caCert', e.target.value)}
            rows={4}
            placeholder="-----BEGIN CERTIFICATE-----"
            disabled={addressLocked}
            data-testid="db-server-form-ca-cert"
          />
        </FormField>
        {isEdit && server.hasCaCert && (
          <Checkbox
            label={t('admin.dbServers.form.removeCaCert')}
            checked={values.removeCaCert}
            disabled={addressLocked}
            onChange={(e) => set('removeCaCert', e.target.checked)}
            data-testid="db-server-form-remove-ca-cert"
          />
        )}

        <FormField
          label={t('admin.dbServers.form.appUser')}
          error={errors.appUser}
          hint={
            appUserLocked
              ? t('admin.dbServers.form.appUserLockedHint')
              : t('admin.dbServers.form.appUserHint')
          }
        >
          <Input
            value={values.appUser}
            onChange={(e) => set('appUser', e.target.value)}
            disabled={appUserLocked}
            data-testid="db-server-form-app-user"
          />
        </FormField>

        <FormField label={t('admin.dbServers.form.notes')} error={errors.notes}>
          <Textarea
            value={values.notes}
            onChange={(e) => set('notes', e.target.value)}
            rows={2}
            maxLength={2000}
            data-testid="db-server-form-notes"
          />
        </FormField>

        {formError && (
          <div
            role="alert"
            className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300"
          >
            {formError}
          </div>
        )}
      </form>
    </Modal>
  );
}
